import React, { useState, useEffect } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { PerformanceGauge } from "../components/PerformanceGauge";
import { PerformanceDashboard } from "../components/PerformanceDashboard";
import { StatusPill } from "../components/StatusPill";
import { BreakGameModal } from "../components/BreakGameModal";
import { EmployeeCalendarWidget } from "../components/EmployeeCalendarWidget";
import { useApp } from "../context/AppContext";
import api from "../api/client";
import { playHydrationChime } from "../push/registerPush";
import { fmtDate } from "../hooks/useClock";
import {
  Zap, ListChecks, LogIn, CheckCircle2, Circle, MapPin, Star, AlertCircle, Gamepad2, Sparkles, Megaphone, PartyPopper, ClipboardList, Clock, CalendarDays, Droplet, ChevronLeft, ChevronRight, Check, Utensils
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function DashboardScreen({ emp, goTab }) {
  if (!emp) return null;
  const [gameModalOpen, setGameModalOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [activeAnnIndex, setActiveAnnIndex] = useState(0);
  const [lunchState, setLunchState] = useState(null);
  const [lunchLoading, setLunchLoading] = useState(false);
  const [myLopRequest, setMyLopRequest] = useState(null);
  const [lopModalOpen, setLopModalOpen] = useState(false);
  const [lopReason, setLopReason] = useState("");
  const [lopSubmitting, setLopSubmitting] = useState(false);
  const [acknowledgedIds, setAcknowledgedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("wibil_acknowledged_announcements") || "[]");
    } catch (e) {
      return [];
    }
  });

  const handleAcknowledge = (id) => {
    const updated = [...acknowledgedIds, id];
    setAcknowledgedIds(updated);
    try {
      localStorage.setItem("wibil_acknowledged_announcements", JSON.stringify(updated));
    } catch (e) {}
  };
  const { tasks, requestLunch, fetchMyLunchRequest } = useApp();
  const myTasks = Array.isArray(tasks) ? tasks : [];
  const recentTasks = myTasks.slice(0, 3);

  useEffect(() => {
    let isMounted = true;
    fetchMyLunchRequest().then(res => {
      if (isMounted && res && res.request) setLunchState(res.request.status);
    }).catch(() => {});
    
    // Fetch my LOP cancellation request status
    api.get("/checkin/cancel-lop-requests/my")
      .then(res => {
        if (isMounted && res.data && res.data.data) {
          setMyLopRequest(res.data.data);
        }
      })
      .catch(err => console.error("Error fetching my LOP request:", err));

    return () => { isMounted = false; };
  }, [fetchMyLunchRequest, emp.checkedIn]);

  useEffect(() => {
    let isMounted = true;
    api.get("/reports/announcements")
      .then(res => {
        if (isMounted && res.data && res.data.announcements) {
          setAnnouncements(res.data.announcements);
        }
      })
      .catch(err => console.error("Error fetching announcements:", err));
    return () => { isMounted = false; };
  }, []);

  const todayStr = new Date().toISOString().split("T")[0];
  const waterKey = `wibil_water_${todayStr}`;
  const [waterIntake, setWaterIntake] = useState(() => {
    try {
      return parseInt(localStorage.getItem(waterKey) || "0", 10);
    } catch(e) { return 0; }
  });

  const handleLogWater = () => {
    const next = waterIntake + 1;
    setWaterIntake(next);
    try { localStorage.setItem(waterKey, String(next)); } catch(e) {}
    playHydrationChime();
  };

  const activeAnnouncements = announcements.filter(a => {
    const end = a.endDate || a.startDate;
    return end >= todayStr;
  });

  // Get current date/time shifted to IST (UTC + 5:30)
  const getClientIST = () => {
    const utc = Date.now();
    return new Date(utc + 19800000);
  };
  const now = getClientIST();
  const isWeekday = now.getUTCDay() >= 1 && now.getUTCDay() <= 5;
  const isBeforeCutoff = now.getUTCHours() < 10 || (now.getUTCHours() === 10 && now.getUTCMinutes() <= 30);
  const showLunch = true; // Always visible as requested

  const handleRequestLunch = async () => {
    if (lunchLoading) return;
    setLunchLoading(true);
    try {
      await requestLunch();
      setLunchState("requested");
    } catch (e) {
      alert("Error requesting lunch: " + (e.response?.data?.error || e.message));
    }
    setLunchLoading(false);
  };

  const handleLopSubmit = async () => {
    if (!lopReason.trim()) return alert("Please enter a reason.");
    setLopSubmitting(true);
    try {
      const res = await api.post("/checkin/cancel-lop-request", { reason: lopReason });
      alert(res.data.message);
      setLopModalOpen(false);
      setLopReason("");
      const reqRes = await api.get("/checkin/cancel-lop-requests/my");
      if (reqRes.data && reqRes.data.data) {
        setMyLopRequest(reqRes.data.data);
      }
    } catch (err) {
      alert(err.response?.data?.error || "Submission failed");
    } finally {
      setLopSubmitting(false);
    }
  };

  const weekData = DAYS.map((day, i) => ({
    day,
    hrs: emp.weeklyHours?.[i] ?? 0,
    target: day === "Sat" ? 5 : day === "Sun" ? 0 : 8,
  }));

  const perfIndex = emp.performanceIndex ?? emp.score ?? 0;

  const isBirthday = (() => {
    if (!emp || !emp.dob) return false;
    const currentMonth = String(now.getUTCMonth() + 1).padStart(2, "0");
    const currentDay = String(now.getUTCDate()).padStart(2, "0");
    const dobParts = emp.dob.split("-");
    if (dobParts.length < 2) return false;
    const dobMonth = dobParts[dobParts.length - 2];
    const dobDay = dobParts[dobParts.length - 1];
    return currentMonth === dobMonth && currentDay === dobDay;
  })();

  return (
    <div>
      {isBirthday && (
        <div style={{
          background: "linear-gradient(135deg, #FF007F, #7F00FF)",
          borderRadius: 20, padding: "24px 20px", color: "#fff",
          marginBottom: 16, textAlign: "center", position: "relative", overflow: "hidden",
          boxShadow: "0 10px 25px rgba(255,0,127,0.3)",
          animation: "fadeSlideIn 0.5s ease-out"
        }}>
          <div style={{ position: "absolute", left: -10, top: -10, fontSize: 32, opacity: 0.35 }}>🎈</div>
          <div style={{ position: "absolute", right: -10, top: 40, fontSize: 32, opacity: 0.35 }}>🎉</div>
          <div style={{ position: "absolute", left: 20, bottom: -10, fontSize: 32, opacity: 0.35 }}>🍰</div>
          <div style={{ position: "absolute", right: 30, bottom: 20, fontSize: 32, opacity: 0.35 }}>✨</div>
          <div style={{ fontSize: 44, marginBottom: 8, filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.15))" }}>🎂</div>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: 0.2 }}>
            Happy Birthday, {emp.name}! 🎂
          </h2>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.9)", margin: "8px 0 0", lineHeight: 1.5, fontWeight: 500 }}>
            Wishing you a wonderful day filled with joy, success, and laughter. Have a fantastic celebration! (DOB: {emp.dob}) 🥳✨
          </p>
        </div>
      )}

      {/* Hero card */}
      <div style={{
        background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
        borderRadius: 20, padding: "20px 18px",
        color: "#fff", position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", right: -28, top: -28,
          width: 130, height: 130, borderRadius: "50%",
          background: "rgba(201,162,39,0.12)",
        }} />
        <div style={{
          position: "absolute", right: 20, bottom: -40,
          width: 90, height: 90, borderRadius: "50%",
          background: "rgba(201,162,39,0.07)",
        }} />
        <div style={{ fontSize: 12, color: "#C7D2E3", letterSpacing: 0.3 }}>
          Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}
        </div>
        <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, marginTop: 2 }}>
          {emp.name}
        </div>
        <div style={{ fontSize: 12, color: "#9FB0C9", marginTop: 2 }}>{emp.role} · {emp.teamName}</div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%",
            background: emp.checkedIn ? "#5FD68B" : "#E8896F",
            boxShadow: emp.checkedIn ? "0 0 0 3px rgba(95,214,139,0.25)" : "none",
          }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {emp.checkedIn
              ? `Checked in at ${emp.checkInTime}`
              : emp.onOD
              ? `On OD — ${emp.odCity}`
              : now.getUTCHours() >= 10
              ? "Absent / LOP (After 10:00 AM)"
              : "Not checked in yet"}
          </span>
        </div>

        {!emp.checkedIn && !emp.onOD && (
          (() => {
            const isAfterTen = now.getUTCHours() >= 10;
            if (!isAfterTen) {
              return (
                <button onClick={() => goTab("checkin")} style={{
                  marginTop: 14, background: TOKENS.gold, color: TOKENS.navyDeep,
                  border: "none", borderRadius: 12, padding: "10px 16px",
                  fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center",
                  gap: 6, cursor: "pointer",
                }}>
                  <LogIn size={15} /> Check in now
                </button>
              );
            } else {
              if (!myLopRequest) {
                return (
                  <button onClick={() => setLopModalOpen(true)} style={{
                    marginTop: 14, background: TOKENS.gold, color: TOKENS.navyDeep,
                    border: "none", borderRadius: 12, padding: "10px 16px",
                    fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center",
                    gap: 6, cursor: "pointer",
                  }}>
                    📋 Request LOP Cancellation
                  </button>
                );
              } else if (myLopRequest.status === "pending") {
                return (
                  <div style={{
                    marginTop: 14, background: "rgba(255,255,255,0.15)", color: "#fff",
                    borderRadius: 12, padding: "10px 16px", fontSize: 12, fontWeight: 600,
                    display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid rgba(255,255,255,0.3)"
                  }}>
                    ⏳ LOP Cancellation Pending Approval
                  </div>
                );
              } else {
                return (
                  <div style={{
                    marginTop: 14, background: "rgba(239,68,68,0.2)", color: "#FCA5A5",
                    borderRadius: 12, padding: "10px 16px", fontSize: 12, fontWeight: 600,
                    display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid rgba(239,68,68,0.4)"
                  }}>
                    ❌ LOP Cancellation Request Rejected
                  </div>
                );
              }
            }
          })()
        )}

        {emp.onOD && (
          <div style={{
            marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(37,99,235,0.3)", borderRadius: 10,
            padding: "5px 12px", fontSize: 12, fontWeight: 600, color: "#93C5FD",
          }}>
            <MapPin size={12} /> On OD in {emp.odCity}
          </div>
        )}
      </div>

      {/* Active Manager Announcements & Daily Reminders Banner */}
      {activeAnnouncements.length > 0 && (() => {
        const annIndex = activeAnnIndex >= activeAnnouncements.length ? 0 : activeAnnIndex;
        const ann = activeAnnouncements[annIndex];
        const isHoliday = ann.type === "holiday";
        const isTask = ann.type === "combined_task";
        const cardBg = isHoliday
          ? `linear-gradient(135deg, ${TOKENS.navyDeep}, #3B0764)`
          : isTask
          ? `linear-gradient(135deg, ${TOKENS.navyDeep}, #1E3A8A)`
          : `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`;

        const isAcknowledged = acknowledgedIds.includes(ann.id);

        return (
          <div style={{ marginTop: 16 }}>
            {/* Styles for dynamic micro-animations */}
            <style>{`
              @keyframes fadeSlideIn {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
              }
              .carousel-card-content {
                animation: fadeSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1);
              }
              .carousel-btn {
                background: #fff;
                border: 1.5px solid ${TOKENS.border};
                color: ${TOKENS.navyDeep};
                border-radius: 50%;
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.2s ease;
                box-shadow: 0 2px 6px rgba(0,0,0,0.06);
                flex-shrink: 0;
              }
              .carousel-btn:hover {
                background: ${TOKENS.cream};
                transform: scale(1.05);
              }
              .carousel-action-btn {
                padding: 6px 12px;
                border-radius: 8px;
                font-size: 11.5px;
                font-weight: 700;
                cursor: pointer;
                transition: all 0.2s ease;
                display: inline-flex;
                align-items: center;
                gap: 5px;
              }
              .carousel-action-btn-primary {
                background: ${TOKENS.gold};
                color: ${TOKENS.navyDeep};
                border: none;
              }
              .carousel-action-btn-primary:hover {
                background: ${TOKENS.goldLight};
                transform: translateY(-1px);
                box-shadow: 0 4px 10px rgba(245, 184, 0, 0.25);
              }
              .carousel-action-btn-secondary {
                background: rgba(255, 255, 255, 0.12);
                color: #fff;
                border: 1px solid rgba(255, 255, 255, 0.2);
              }
              .carousel-action-btn-secondary:hover {
                background: rgba(255, 255, 255, 0.2);
                transform: translateY(-1px);
              }
            `}</style>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: TOKENS.navyDeep, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 6 }}>
                <Megaphone size={14} color={TOKENS.goldDark} /> Active Manager Notices & Reminders
              </span>
              <span style={{ fontSize: 10, fontWeight: 700, color: TOKENS.goldDark, background: "rgba(245,184,0,0.15)", padding: "2px 8px", borderRadius: 8 }}>
                {annIndex + 1} / {activeAnnouncements.length} Active
              </span>
            </div>

            {/* Slider Wrap */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* Prev Button */}
              {activeAnnouncements.length > 1 && (
                <button
                  className="carousel-btn"
                  onClick={() => setActiveAnnIndex(prev => (prev - 1 + activeAnnouncements.length) % activeAnnouncements.length)}
                  title="Previous Notice"
                >
                  <ChevronLeft size={16} />
                </button>
              )}

              {/* Main Card */}
              <Card 
                className="carousel-card-content" 
                key={ann.id} 
                style={{
                  flex: 1,
                  padding: "16px 18px",
                  background: cardBg,
                  color: "#fff",
                  borderRadius: 16,
                  border: `1.5px solid ${isHoliday ? TOKENS.gold : isTask ? "#60A5FA" : TOKENS.goldPale}`,
                  boxShadow: "0 4px 14px rgba(10, 31, 58, 0.15)",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden"
                }}
              >
                {/* Header Row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 800, color: TOKENS.navyDeep,
                    background: isHoliday ? "#FDE047" : isTask ? "#93C5FD" : "#E9D5FF",
                    padding: "3px 10px", borderRadius: 10, textTransform: "uppercase", letterSpacing: 0.5,
                    display: "inline-flex", alignItems: "center", gap: 4
                  }}>
                    {isHoliday ? <PartyPopper size={12} /> : isTask ? <ClipboardList size={12} /> : <Megaphone size={12} />}
                    {isHoliday ? "Holiday Announcement" : isTask ? "Combined Team Task" : "Notice"}
                  </span>

                  <span style={{ fontSize: 11, color: TOKENS.goldLight, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                    <Clock size={12} /> Active till {fmtDate(ann.endDate || ann.startDate)}
                  </span>
                </div>

                {/* Content */}
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700 }}>
                  {ann.title}
                </div>

                {ann.description && (
                  <div style={{ fontSize: 12, color: "#E2E8F0", marginTop: 4, lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                    {ann.description}
                  </div>
                )}

                {/* Interactive Action Footer Panel */}
                <div style={{ 
                  marginTop: 14, 
                  paddingTop: 12, 
                  borderTop: "1px solid rgba(255, 255, 255, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 8
                }}>
                  {/* Left Side: Acknowledge button */}
                  <div>
                    {isAcknowledged ? (
                      <span style={{ 
                        fontSize: 11, 
                        fontWeight: 700, 
                        color: "#4ADE80", 
                        background: "rgba(74, 222, 128, 0.15)", 
                        padding: "4px 10px", 
                        borderRadius: 8,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4
                      }}>
                        <CheckCircle2 size={13} color="#4ADE80" /> Acknowledged ✓
                      </span>
                    ) : (
                      <button
                        className="carousel-action-btn carousel-action-btn-primary"
                        onClick={() => handleAcknowledge(ann.id)}
                      >
                        <Check size={13} /> Acknowledge Notice
                      </button>
                    )}
                  </div>

                  {/* Right Side: Specific actions */}
                  <div style={{ display: "flex", gap: 8 }}>
                    {isTask && (
                      <button
                        className="carousel-action-btn carousel-action-btn-secondary"
                        onClick={() => goTab("tasks")}
                      >
                        <ClipboardList size={13} /> Go to Tasks
                      </button>
                    )}
                  </div>
                </div>
              </Card>

              {/* Next Button */}
              {activeAnnouncements.length > 1 && (
                <button
                  className="carousel-btn"
                  onClick={() => setActiveAnnIndex(prev => (prev + 1) % activeAnnouncements.length)}
                  title="Next Notice"
                >
                  <ChevronRight size={16} />
                </button>
              )}
            </div>

            {/* Dots navigation indicator */}
            {activeAnnouncements.length > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 8 }}>
                {activeAnnouncements.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveAnnIndex(idx)}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      border: "none",
                      background: annIndex === idx ? TOKENS.gold : "rgba(10, 25, 50, 0.15)",
                      cursor: "pointer",
                      padding: 0,
                      transition: "all 0.2s ease"
                    }}
                    title={`Go to notice ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Gauge + stats row */}
      <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
        <Card style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", padding: 12 }}>
          <PerformanceGauge score={perfIndex} size={124} label="Performance" />
        </Card>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
          <Card style={{ padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
              <Zap size={14} color={TOKENS.gold} />
              <span style={{ fontSize: 10, color: TOKENS.muted, fontWeight: 700, letterSpacing: 0.5 }}>STREAK</span>
            </div>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.navyDeep }}>
              {emp.streak} <span style={{ fontSize: 14, fontWeight: 400 }}>days</span>
            </div>
          </Card>
          <Card style={{ padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
              <ListChecks size={14} color={TOKENS.gold} />
              <span style={{ fontSize: 10, color: TOKENS.muted, fontWeight: 700, letterSpacing: 0.5 }}>TASKS TODAY</span>
            </div>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.navyDeep }}>
              {emp.tasksToday?.done || 0}
              <span style={{ fontSize: 14, color: TOKENS.muted, fontWeight: 400 }}>/{emp.tasksToday?.total || 0}</span>
            </div>
          </Card>
        </div>
      </div>

      {/* Interactive Employee Calendar Widget */}
      <EmployeeCalendarWidget announcements={announcements} />

      {/* Lunch Request Card */}
      {showLunch && (
        <Card style={{
          marginTop: 16,
          padding: "16px 20px",
          background: `linear-gradient(135deg, ${TOKENS.navySoft}, ${TOKENS.navyDeep})`,
          color: "#fff",
          borderRadius: 18,
          border: `1.5px solid rgba(255, 255, 255, 0.1)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 220 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: "rgba(255,255,255,0.15)", border: `1px solid rgba(255,255,255,0.3)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", flexShrink: 0
            }}>
              <Utensils size={24} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 700 }}>Daily Lunch</span>
              </div>
              <div style={{ fontSize: 12, color: "#9FB0C9", marginTop: 2 }}>
                Request your lunch before 10:30 AM
              </div>
            </div>
          </div>
          <button
            onClick={handleRequestLunch}
            disabled={["requested", "approved", "rejected"].includes(lunchState) || lunchLoading || (!isWeekday || !isBeforeCutoff)}
            style={{
              padding: "10px 20px",
              borderRadius: 24,
              border: "none",
              fontWeight: 700,
              fontSize: 13,
              cursor: (["requested", "approved", "rejected"].includes(lunchState) || (!isWeekday || !isBeforeCutoff)) ? "not-allowed" : "pointer",
              background: lunchState === "approved" ? TOKENS.successBg : lunchState === "rejected" ? TOKENS.dangerBg : lunchState === "requested" ? "rgba(255,255,255,0.2)" : (!isWeekday || !isBeforeCutoff) ? "rgba(255,255,255,0.1)" : TOKENS.gold,
              color: lunchState === "approved" ? TOKENS.success : lunchState === "rejected" ? TOKENS.danger : lunchState === "requested" ? "#fff" : (!isWeekday || !isBeforeCutoff) ? TOKENS.muted : TOKENS.navyDeep,
              transition: "transform 0.2s, background 0.2s"
            }}
          >
            {lunchLoading ? "Loading..." : lunchState === "approved" ? "Approved" : lunchState === "rejected" ? "Rejected" : lunchState === "requested" ? "Requested" : (!isWeekday || !isBeforeCutoff) ? "Closed" : "Request Lunch"}
          </button>
        </Card>
      )}

      {/* 5-Min Break Blitz Card */}
      <Card style={{
        marginTop: 16,
        padding: "16px 20px",
        background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
        color: "#fff",
        borderRadius: 18,
        border: `1.5px solid rgba(201, 162, 39, 0.35)`,
        boxShadow: "0 4px 16px rgba(10, 31, 58, 0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 220 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: "rgba(201,162,39,0.2)", border: `1px solid ${TOKENS.gold}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: TOKENS.goldLight, flexShrink: 0
          }}>
            <Gamepad2 size={24} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 700 }}>5-Min Break Blitz</span>
              <span style={{ fontSize: 10, background: "rgba(201,162,39,0.25)", color: TOKENS.goldLight, padding: "2px 8px", borderRadius: 10, fontWeight: 700 }}>
                RECHARGE
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#9FB0C9", marginTop: 2 }}>
              Take a quick 5-min mental break, refresh your focus & crush your targets!
            </div>
          </div>
        </div>
        <button
          onClick={() => setGameModalOpen(true)}
          style={{
            background: `linear-gradient(135deg, #ffe066 0%, #f5b800 50%, #d49b00 100%)`,
            color: TOKENS.navyDeep,
            border: "none",
            borderRadius: 12,
            padding: "10px 18px",
            fontWeight: 800,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(245, 184, 0, 0.35)",
            transition: "transform 0.2s"
          }}
        >
          <Sparkles size={16} /> Play 5-Min Break
        </button>
      </Card>

      {/* 2-Hour Hydration Reminder Card */}
      <Card style={{
        marginTop: 16,
        padding: "16px 20px",
        background: "linear-gradient(135deg, #0284C7 0%, #0369A1 100%)",
        color: "#fff",
        borderRadius: 18,
        border: "1.5px solid rgba(125, 211, 252, 0.4)",
        boxShadow: "0 4px 16px rgba(2, 132, 199, 0.25)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 220 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: "rgba(255, 255, 255, 0.2)",
            border: "1px solid rgba(255, 255, 255, 0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#BAE6FD", flexShrink: 0
          }}>
            <Droplet size={24} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 700 }}>2-Hour Hydration Reminder</span>
              <span style={{ fontSize: 10, background: "rgba(255, 255, 255, 0.25)", color: "#E0F2FE", padding: "2px 8px", borderRadius: 10, fontWeight: 800 }}>
                EVERY 2 HRS
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#E0F2FE", marginTop: 2 }}>
              Target: 8 Glasses/Day · Logged: <strong>{waterIntake}/8 Glasses</strong>
            </div>
          </div>
        </div>

        <button
          onClick={handleLogWater}
          style={{
            background: "#ffffff",
            color: "#0369A1",
            border: "none",
            borderRadius: 12,
            padding: "10px 18px",
            fontWeight: 800,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            transition: "transform 0.15s"
          }}
        >
          <Droplet size={16} fill="#0284C7" color="#0284C7" /> +1 Glass Water 🥤
        </button>
      </Card>

      {/* Grade Bands & Incentive Action Card */}
      <PerformanceDashboard emp={emp} />

      {/* Weekly hours */}
      <SectionLabel>This week's hours</SectionLabel>
      <Card style={{ padding: "16px 8px 8px" }}>
        <ResponsiveContainer width="100%" height={130}>
          <BarChart data={weekData} barSize={14}>
            <CartesianGrid vertical={false} stroke="#F0ECE1" />
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: TOKENS.muted }} axisLine={false} tickLine={false} />
            <YAxis hide />
            <Tooltip
              cursor={{ fill: "rgba(201,162,39,0.08)" }}
              contentStyle={{ fontFamily: "Inter", fontSize: 12, borderRadius: 10, border: `1px solid ${TOKENS.border}` }}
            />
            <Bar dataKey="hrs" radius={[5, 5, 0, 0]} fill={TOKENS.gold} />
            <Bar dataKey="target" radius={[5, 5, 0, 0]} fill="#E9E3D2" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Recent activity */}
      <SectionLabel>Recent activity</SectionLabel>
      <Card style={{ padding: 0 }}>
        {recentTasks.map((t, i) => (
          <div key={t.id} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "13px 16px",
            borderBottom: i < recentTasks.length - 1 ? `1px solid ${TOKENS.border}` : "none",
          }}>
            {t.status === "done"
              ? <CheckCircle2 size={17} color={TOKENS.success} />
              : <Circle size={17} color={TOKENS.muted} />
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TOKENS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.title}
              </div>
              <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 1 }}>{t.location}</div>
            </div>
            <StatusPill status={t.status} />
          </div>
        ))}
      </Card>

      <BreakGameModal
        isOpen={gameModalOpen}
        onClose={() => setGameModalOpen(false)}
        onScoreUpdated={(data) => {
          console.log("Break game completed:", data);
        }}
      />

      {/* LOP Cancellation Request Modal */}
      {lopModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(10,25,47,0.7)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyItems: "center",
          alignContent: "center", justifyContent: "center",
          padding: 20, backdropFilter: "blur(4px)",
        }}>
          <Card style={{ width: "100%", maxWidth: 400, padding: 24, background: "#fff", borderRadius: 16 }}>
            <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: "0 0 8px 0" }}>
              Request LOP Cancellation
            </h3>
            <p style={{ fontSize: 12.5, color: TOKENS.muted, margin: "0 0 16px 0", lineHeight: 1.4 }}>
              You are logging in after 10:00 AM Indian Standard Time. Please enter the reason for your late arrival to request absent/LOP cancellation.
            </p>
            <textarea
              value={lopReason}
              onChange={(e) => setLopReason(e.target.value)}
              placeholder="Enter your reason here..."
              rows={4}
              style={{
                width: "100%", padding: 12, borderRadius: 10, border: `1.5px solid ${TOKENS.border}`,
                fontSize: 13, fontFamily: "Inter, sans-serif", resize: "none", boxSizing: "border-box",
                outline: "none"
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button
                onClick={() => { setLopModalOpen(false); setLopReason(""); }}
                style={{
                  background: "transparent", border: "none", color: TOKENS.muted,
                  fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "8px 12px"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLopSubmit}
                disabled={lopSubmitting || !lopReason.trim()}
                style={{
                  background: TOKENS.gold, color: TOKENS.navyDeep, border: "none",
                  borderRadius: 10, padding: "8px 16px", fontSize: 13, fontWeight: 700,
                  cursor: (lopSubmitting || !lopReason.trim()) ? "not-allowed" : "pointer",
                  opacity: (lopSubmitting || !lopReason.trim()) ? 0.6 : 1,
                  boxShadow: "0 2px 8px rgba(245,184,0,0.2)"
                }}
              >
                {lopSubmitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
